/**
 * Demo / screen-recording feature flags for the replay flow.
 *
 * The replay UI normally swaps the bottom multimodal input bar for a
 * `<ReplayControls>` strip (pause / play / skip / speed / continue).
 * That's right for ordinary visitors — but when the founder is screen-
 * recording a demo for an influencer, they want the bottom of the
 * page to look like the *real* product (a normal input bar) so the
 * recording doesn't scream "this is a replay". These flags enable
 * that mode and the related keyword router.
 *
 * Flags resolve in this priority order (first match wins):
 *   1. URL query param  — `?demo_input=1` / `?demo_input=0`
 *   2. localStorage     — `xynth.demo_input` = "1" / "0"
 *   3. Build-time env   — `NEXT_PUBLIC_DEMO_REPLAY_INPUT`
 *
 * Why three layers: env is for permanent staging/production toggling,
 * localStorage persists across navigations within a recording session,
 * and the URL param is the fast escape hatch (e.g. when wiring up a
 * pre-recorded link to share with someone).
 *
 * Both flags are READ HOOKS — they only run client-side and only on
 * mount, so SSR doesn't see them. That's fine for our use case (replay
 * is always a client-side experience).
 */

import { useEffect, useState } from "react";

// ── Storage keys ──────────────────────────────────────────────────────
const LS_DEMO_INPUT = "xynth.demo_input";
const LS_KEYWORD_ROUTE = "xynth.keyword_route";

// ── Build-time env flags ──────────────────────────────────────────────
// Both default to **true** so screen-recording just works out of the
// box — no env var, no localStorage, no URL param needed. The 3-layer
// resolver below still honours an explicit `?demo_input=0` /
// `?keyword_route=0` escape hatch for the rare case we want to view a
// replay in its classic playback-controls mode, so we lose nothing.
// If a build-time env var is set to "0", it disables the flag; any
// other value (including unset) falls through to the default-on.
const ENV_DEMO_INPUT = process.env.NEXT_PUBLIC_DEMO_REPLAY_INPUT !== "0";
const ENV_KEYWORD_ROUTE = process.env.NEXT_PUBLIC_DEMO_KEYWORD_ROUTING !== "0";

// ── Keyword → curated replay map ──────────────────────────────────────
// EDIT ME when you record new demos.
//
// Match is case-insensitive, whole-substring (i.e. `keyword` appears
// anywhere in the user's typed prompt). Order matters: the FIRST match
// wins, so put more specific keywords first if there's any overlap.
//
// Keep keywords distinctive — common words like "alert" or "stock"
// would route every prompt and break the regular fallback flow. The
// founder uses code-name style triggers (e.g. "ginger") so they only
// fire when intentional.
export interface KeywordReplayRoute {
  keyword: string;
  curatedId: string;
  description: string;
}

export const KEYWORD_REPLAY_ROUTES: readonly KeywordReplayRoute[] = [
  {
    // Phrase chosen so it sits naturally inside a conversational
    // prompt the founder would actually type during a recording —
    // e.g. "What are AI stocks doing pre-market, any worthy setups?
    // Alert me every day at 9 AM if you find any" — instead of a
    // dummy code-name.
    keyword: "ai stocks doing pre-market",
    curatedId: "ai_stack_premarket_alert",
    description: "AI Stack Pre-Market Alert demo",
  },
  {
    // Signal Hunter alert — @TukiFromKL's US-vs-non-US AI closed/open
    // cohort thesis + SaaS-pocalypse ripple. Keyword is a verbatim
    // substring of the conversational prompt rendered on the replay
    // page: "build me a daily alert that scans ai stocks for the best
    // setups. show me how us ai names are trading versus the non-us
    // ones, where saas is cracking or bouncing, a grid of the top
    // setups right now, and end with today's best trade with real
    // entry, stop, target and an options play."
    keyword: "us ai names are trading",
    curatedId: "us_vs_non_us_ai_scan",
    description: "Signal Hunter — US vs non-US AI + SaaS ripple (TukiFromKL)",
  },
  {
    // Venu's AI Stack Pulse launch — daily 4:15pm post-close digest
    // of the 5 AI layers (chips, hyperscalers, models, apps, power)
    // with movers and best-swing options play. Keyword sits verbatim
    // inside the natural prompt — sector-rotation framing in trader
    // vocabulary, matches Venu's "thematic investor + swing trader"
    // positioning:
    // "Where's the rotation in the AI stack today? Alert me every
    // day at 4:15 with the layer leader and the best swing."
    keyword: "rotation in the ai stack",
    curatedId: "ai_stack_pulse_daily_alert",
    description: "AI Stack Pulse (Venu) — sector rotation + best swing",
  },
  {
    // Wall St Engine's AI Scoreboard Pre-Market — US/non-US cohort
    // divergence over 60d + insider buys + earnings + best long/short
    // with options. Keyword sits verbatim inside the natural prompt:
    // "Which AI stocks are moving before the open and what's the best
    // trade today ? Find the best setups and e-mail me every day at
    // 8:30 with the best ones"
    keyword: "ai stocks are moving",
    curatedId: "wallstengine_ai_scoreboard",
    description: "AI Scoreboard Pre-Market (Wall St Engine) — divergence + insider pulse + best long/short",
  },
  {
    // AI Breakfast's AI Heatmap Dashboard — daily pre-market 90-stock AI
    // stack dashboard with market cap treemap, Twitter/news sentiment on
    // gappers, options flow on whales, technicals on top movers, and
    // earnings calendar. Keyword sits verbatim inside the natural prompt:
    // "What's moving in AI pre-market? Show me a daily heatmap with the
    // whole stack, flow, sentiment, and earnings. Email it every morning
    // at 9."
    keyword: "daily heatmap with the whole stack",
    curatedId: "aibreakfast_ai_heatmap",
    description: "AI Heatmap Dashboard (AI Breakfast) — 90-stock treemap + flow + sentiment + earnings",
  },
  {
    // Market Edge Scan demo — 4-cell market-wide options setup walkthrough
    // (UW-style market map → top-5 template sweep → X chatter & news →
    // 3 defined-risk trades) followed by a 2nd turn promoting the workflow
    // to a 9 AM daily alert. Keyword sits verbatim inside the natural
    // founder-voice prompt: "yo find me the cleanest options setups in
    // the market for next week, scan the whole thing and give me a few
    // real trades i can put on"
    keyword: "cleanest options setups",
    curatedId: "market_edge_scan_setups",
    description: "Market Edge Scan — top options plays across the market + daily alert",
  },
] as const;

/**
 * Returns the matching curated replay ID for a typed prompt, or null
 * if no keyword matched. Case-insensitive, whole-substring.
 */
export function findKeywordReplayRoute(
  text: string | null | undefined,
): KeywordReplayRoute | null {
  if (!text) return null;
  const lower = text.toLowerCase();
  for (const route of KEYWORD_REPLAY_ROUTES) {
    if (lower.includes(route.keyword.toLowerCase())) {
      return route;
    }
  }
  return null;
}

// ── Flag readers ──────────────────────────────────────────────────────
function _readFlag(
  qsKey: string,
  lsKey: string,
  envDefault: boolean,
): boolean {
  if (typeof window === "undefined") return envDefault;
  // 1. URL param wins.
  const params = new URLSearchParams(window.location.search);
  const qs = params.get(qsKey);
  if (qs === "1" || qs === "true") return true;
  if (qs === "0" || qs === "false") return false;
  // 2. localStorage next.
  try {
    const ls = window.localStorage.getItem(lsKey);
    if (ls === "1") return true;
    if (ls === "0") return false;
  } catch {
    // localStorage can throw in private mode; fall through.
  }
  // 3. Env default.
  return envDefault;
}

/**
 * React hook: returns true when the demo input bar should replace the
 * normal `<ReplayControls>` during a replay session. Re-reads on mount
 * so URL params are live; doesn't watch storage events because nothing
 * in the app mutates these flags after mount.
 */
export function useDemoReplayInputEnabled(): boolean {
  const [enabled, setEnabled] = useState<boolean>(false);
  useEffect(() => {
    setEnabled(_readFlag("demo_input", LS_DEMO_INPUT, ENV_DEMO_INPUT));
  }, []);
  return enabled;
}

/**
 * React hook: returns true when typed prompts should be checked
 * against `KEYWORD_REPLAY_ROUTES` and re-routed to a replay. Same
 * three-layer flag resolution as `useDemoReplayInputEnabled`.
 */
export function useKeywordReplayRoutingEnabled(): boolean {
  const [enabled, setEnabled] = useState<boolean>(false);
  useEffect(() => {
    setEnabled(_readFlag("keyword_route", LS_KEYWORD_ROUTE, ENV_KEYWORD_ROUTE));
  }, []);
  return enabled;
}

/**
 * Synchronous, hook-less reader for the keyword-route flag. Use this
 * inside event handlers (e.g. `submitForm`) where the React useState
 * value from `useKeywordReplayRoutingEnabled` may still be its initial
 * `false` because `useEffect` hasn't yet flushed — happens reliably on
 * HMR remounts and on the very first paint of a fresh /chat page.
 *
 * Reading window/localStorage on every call is fine: this only fires
 * on user submit (one read per send), not in render, so SSR safety
 * isn't a concern.
 */
export function readKeywordReplayRoutingEnabled(): boolean {
  return _readFlag("keyword_route", LS_KEYWORD_ROUTE, ENV_KEYWORD_ROUTE);
}
